// /admin 의 공지 패널 — config/announcement 를 직접 쓴다 (규칙이 관리자 이메일에만
// 쓰기를 연다). 영어만 쓰고 게시하면 트리거 onAnnouncementWrite 가 1분 안에 나머지
// 언어를 채우고, 이 패널이 문서를 구독하고 있어 번역이 들어오는 게 바로 보인다.
// 필드 의미는 functions/announcement.js 맨 위. CLI 는 scripts/announce.cjs.
import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase.js';
import { SUPPORTED_LANGS, LANG_LABELS } from '../hooks/useLocale.jsx';

const REF = () => doc(db, 'config', 'announcement');
const DESTS = [
  ['', '버튼 없음 (닫기만)'],
  ['/gio', '디자이너 (Gio)'],
  ['/gallery', '갤러리'],
  ['/create', '만들기'],
  ['/designs', '내 페이지'],
  ['/invite', '초대'],
  ['custom', '직접 입력…'],
];
const PLATFORMS = [['ios', 'iOS'], ['android', 'Android'], ['web', '웹']];

const newId = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
};
// datetime-local ↔ ISO (브라우저 시간대 기준)
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

const EMPTY = {
  id: '', title: '', body: '', ctaLabel: '', dest: '', customTo: '',
  from: '', until: '', platforms: [], audience: 'all', maxVersion: '',
};

function formFrom(d) {
  if (!d) return { ...EMPTY, id: newId() };
  const to = d.cta?.to || '';
  const known = DESTS.some(([v]) => v === to && v !== 'custom');
  return {
    id: d.id || newId(),
    title: d.title?.en || '',
    body: d.body?.en || '',
    ctaLabel: d.cta?.label?.en || '',
    dest: to ? (known ? to : 'custom') : '',
    customTo: known ? '' : to,
    from: toLocalInput(d.from),
    until: toLocalInput(d.until),
    platforms: Array.isArray(d.platforms) ? d.platforms : [],
    audience: d.audience || 'all',
    maxVersion: d.maxVersion || '',
  };
}

export function AnnouncementAdmin() {
  const [cur, setCur] = useState(undefined); // undefined = 로딩, null = 문서 없음
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [edits, setEdits] = useState({}); // { 'title.ko': '...' }

  useEffect(() => onSnapshot(REF(), (snap) => {
    const d = snap.exists() ? snap.data() : null;
    setCur(d);
    setForm((f) => f || formFrom(d));
  }, (e) => setMsg(`읽기 실패: ${e.message}`)), []);

  if (cur === undefined || !form) return <section className="adm-panel"><h2>공지</h2><p className="adm-note">불러오는 중…</p></section>;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const to = form.dest === 'custom' ? form.customTo.trim() : form.dest;
  const toOk = !to || /^(\/|https:\/\/)/.test(to);
  const canPublish = form.id.trim() && form.title.trim() && toOk && (!to || form.ctaLabel.trim());

  const publish = async () => {
    if (!canPublish) return;
    const sameId = cur?.id === form.id.trim();
    if (sameId && !window.confirm('같은 id 로 게시하면 이미 닫은 사람에겐 다시 안 뜹니다. 그대로 게시할까요?\n(다시 보이게 하려면 id 를 바꾸세요)')) return;
    setBusy(true); setMsg(null);
    try {
      await setDoc(REF(), {
        active: true,
        id: form.id.trim(),
        title: { en: form.title.trim() },
        body: form.body.trim() ? { en: form.body.trim() } : null,
        cta: to ? { label: { en: form.ctaLabel.trim() }, to } : null,
        from: fromLocalInput(form.from),
        until: fromLocalInput(form.until),
        platforms: form.platforms.length ? form.platforms : null,
        audience: form.audience,
        maxVersion: form.maxVersion.trim() || null,
        _src: {},
        updatedAt: serverTimestamp(),
      });
      setEdits({});
      setMsg('게시했습니다. 번역은 1분 안에 아래 표에 채워집니다. 앱은 최대 5분 뒤 반영.');
    } catch (e) {
      setMsg(`게시 실패: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const takeDown = async () => {
    setBusy(true); setMsg(null);
    try {
      await updateDoc(REF(), { active: false, updatedAt: serverTimestamp() });
      setMsg('내렸습니다.');
    } catch (e) {
      setMsg(`내리기 실패: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const saveEdits = async () => {
    const fields = Object.fromEntries(Object.entries(edits).filter(([, v]) => typeof v === 'string'));
    if (!Object.keys(fields).length) return;
    setBusy(true); setMsg(null);
    try {
      await updateDoc(REF(), { ...fields, updatedAt: serverTimestamp() });
      setEdits({});
      setMsg('번역 수정을 저장했습니다.');
    } catch (e) {
      setMsg(`저장 실패: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const status = !cur ? '공지 없음'
    : cur.active ? `게시 중 · ${cur.id}` : `내려감 · ${cur.id}`;
  const rows = [
    ['title', '제목', cur?.title],
    ['body', '본문', cur?.body],
    ['cta.label', '버튼', cur?.cta?.label],
  ].filter(([, , m]) => m && m.en);
  const translating = cur && rows.some(([, , m]) => SUPPORTED_LANGS.some((l) => !m[l]));

  return (
    <section className="adm-panel adm-announce">
      <h2>앱 공지 <span className="adm-sub">{status} · 영어로 쓰면 나머지 언어는 자동 번역 · 2.3.0 이상 앱 + 웹</span></h2>

      <div className="adm-form">
        <label>id <small>한 번 닫은 사람에겐 같은 id 가 다시 안 뜬다</small>
          <div className="adm-row">
            <input value={form.id} onChange={set('id')} />
            <button type="button" className="adm-btn-ghost" onClick={() => setForm((f) => ({ ...f, id: newId() }))}>새 id</button>
          </div>
        </label>
        <label>제목 (영어)
          <input value={form.title} onChange={set('title')} maxLength={120} placeholder="Meet Gio, your designer" />
        </label>
        <label>본문 (영어, 선택)
          <textarea rows={3} value={form.body} onChange={set('body')} maxLength={600} />
        </label>
        <div className="adm-row">
          <label>버튼이 여는 곳
            <select value={form.dest} onChange={set('dest')}>
              {DESTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          {form.dest === 'custom' && (
            <label>주소 <small>/경로 또는 https://</small>
              <input value={form.customTo} onChange={set('customTo')} placeholder="https://…" />
            </label>
          )}
          {form.dest && (
            <label>버튼 문구 (영어)
              <input value={form.ctaLabel} onChange={set('ctaLabel')} maxLength={40} placeholder="Try Gio" />
            </label>
          )}
        </div>
        <div className="adm-row">
          <label>시작 (선택)<input type="datetime-local" value={form.from} onChange={set('from')} /></label>
          <label>끝 (선택)<input type="datetime-local" value={form.until} onChange={set('until')} /></label>
        </div>
        <div className="adm-row">
          <fieldset>
            <legend>플랫폼 <small>아무것도 안 고르면 전부</small></legend>
            {PLATFORMS.map(([v, l]) => (
              <label key={v} className="adm-check">
                <input
                  type="checkbox"
                  checked={form.platforms.includes(v)}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    platforms: e.target.checked ? [...f.platforms, v] : f.platforms.filter((p) => p !== v),
                  }))}
                />{l}
              </label>
            ))}
          </fieldset>
          <label>대상
            <select value={form.audience} onChange={set('audience')}>
              <option value="all">모두</option>
              <option value="signedIn">로그인 사용자</option>
              <option value="guest">게스트</option>
            </select>
          </label>
          <label>앱 버전 이하만 <small>업데이트 권유용, 예: 2.3.0</small>
            <input value={form.maxVersion} onChange={set('maxVersion')} placeholder="" />
          </label>
        </div>
        {!toOk && <p className="adm-error">주소는 / 또는 https:// 로 시작해야 합니다.</p>}
        <div className="adm-row">
          <button type="button" className="btn btn-primary" disabled={!canPublish || busy} onClick={publish}>게시</button>
          {cur?.active && <button type="button" className="btn btn-secondary" disabled={busy} onClick={takeDown}>내리기</button>}
        </div>
        {msg && <p className="adm-note">{msg}</p>}
      </div>

      {rows.length > 0 && (
        <>
          <h3 className="adm-h3">언어별 문구 {translating && <span className="adm-sub">번역 중…</span>}</h3>
          <div className="adm-table-wrap">
            <table className="adm-table adm-announce-table">
              <thead><tr><th>언어</th>{rows.map(([k, l]) => <th key={k}>{l}</th>)}</tr></thead>
              <tbody>
                {SUPPORTED_LANGS.map((lang) => (
                  <tr key={lang}>
                    <td>{LANG_LABELS[lang] || lang}</td>
                    {rows.map(([k, , m]) => {
                      const key = `${k}.${lang}`;
                      return (
                        <td key={k}>
                          {lang === 'en'
                            ? m.en
                            : <textarea
                                rows={2}
                                value={edits[key] ?? m[lang] ?? ''}
                                placeholder="번역 대기"
                                onChange={(e) => setEdits((x) => ({ ...x, [key]: e.target.value }))}
                              />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="adm-row">
            <button type="button" className="btn btn-secondary" disabled={busy || !Object.keys(edits).length} onClick={saveEdits}>번역 수정 저장</button>
            <p className="adm-note">영어를 바꿔 다시 게시하면 손으로 고친 번역은 새로 번역된다.</p>
          </div>
        </>
      )}
    </section>
  );
}
